import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  useTheme
} from '@mui/material';
import { paintMGCCodeDesc, paintMGCs } from './UtilFunctions';

export default function MGCViewOnly(props) {
  const theme = useTheme();
  const isMgcEdited = props.isMgcEdited;
  const mgcs =
    props.selectedMgc && props.selectedMgc.commodity && isMgcEdited
      ? buildMgcsForEditedMgc()
      : null;
  const strategyInformation = props.strategyInformation;
  const selectedData = isMgcEdited
    ? props.selectedMgc && props.selectedMgc.commodity
      ? mgcs
      : null
    : strategyInformation.mgcs;
  const masterData = props.masterData;

  function buildMgcsForEditedMgc() {
    let mgcsTemp = [];
    if (props.selectedMgc) {
      if (
        props.selectedMgc.minorSegments != null &&
        props.selectedMgc.minorSegments.length > 0
      ) {
        props.selectedMgc.minorSegments.forEach((msg) => {
          if (msg.children && !msg.checked) {
            msg.children.forEach((seg) => {
              if (seg.checked === true) {
                mgcsTemp.push({
                  commodity: props.selectedMgc.commodity
                    ? props.selectedMgc.commodity.value
                    : '',
                  commodityDesc: props.selectedMgc.commodity
                    ? props.selectedMgc.commodity.desc
                    : '',
                  major: props.selectedMgc.major
                    ? props.selectedMgc.major.value
                    : '',
                  majorDesc: props.selectedMgc.major
                    ? props.selectedMgc.major.desc
                    : '',
                  minor: seg.parent.code,
                  minorName: seg.parent.name,
                  segment: seg.code,
                  segmentName: seg.name
                });
              }
            });
          } else if (msg.checked) {
            mgcsTemp.push({
              commodity: props.selectedMgc.commodity
                ? props.selectedMgc.commodity.value
                : '',
              major: props.selectedMgc.major
                ? props.selectedMgc.major.value
                : '',
              minor: msg.code,
              segment: null,
              segmentName: null
            });
          }
        });
      }
      if (mgcsTemp.length === 0) {
        mgcsTemp.push({
          commodity: props.selectedMgc.commodity
            ? props.selectedMgc.commodity.value
            : '',
          major: props.selectedMgc.major ? props.selectedMgc.major.value : '',
          minor: null,
          segment: null,
          segmentName: null
        });
      }
    }
    return mgcsTemp;
  }

  function findMasterMGCInfoForSelection(masterMGCs, selectedMGCs) {
    if (masterMGCs && selectedMGCs) {
      let commodity = masterMGCs.find(
        (i) => i.code === selectedMGCs[0].commodity
      );
      let major =
        commodity &&
        commodity.majors?.find((i) => i.code === selectedMGCs[0].major);
      let commMajorCode =
        commodity && commodity.code + ' ' + selectedMGCs[0].major;
      let commMajorDesc = major?.name
        ? commodity.name + ' ' + major.name
        : 'INVALID MGC';

      return {
        major: major,
        commMajorCode: commMajorCode,
        commMajorDesc: commMajorDesc
      };
    }
  }
  function getMGCDefinitionLevelAndSelections(mgcs) {
    let majorLevel, minorLevel, segmentLevel;
    let minorMap = new Map(); // Key is Minor Code and Value is Map of Segment Codes
    majorLevel = minorLevel = segmentLevel = false;

    mgcs &&
      mgcs.forEach((mgc) => {
        if (mgc.segment) {
          segmentLevel = true;

          let segMap = minorMap.get(mgc.minor);

          if (!segMap) {
            segMap = new Map();
            // Add to minor map - value (Segment Map)
            minorMap.set(mgc.minor, segMap);
          }

          // Add to SegMap of Minor
          segMap.set(mgc.segment, mgc.segment);
        } else if (mgc.minor) {
          minorLevel = true;
          // Add to minor map - No Segment Map (its Null)
          minorMap.set(mgc.minor, null); // Object is null
        } else majorLevel = true;
      });

    return {
      majorLevel: majorLevel && !minorLevel && !segmentLevel,
      minorLevel: !majorLevel && minorLevel && !segmentLevel,
      segmentLevel: !majorLevel && !minorLevel && segmentLevel,
      minorMap: minorMap
    };
  }
  function paintSelectedAndNoTSelectedSegments(
    children,
    selectedChildrenMap,
    precedingCode,
    precedingName,
    showAsInactive,
    condition
  ) {
    return (
      <ul>
        {children.map(
          (segment) =>
            selectedChildrenMap &&
            selectedChildrenMap.has(segment.code) === condition && (
              <li
                style={
                  showAsInactive
                    ? { color: theme.fuel6Tokens.JddcColorTextSecondary }
                    : {}
                }
              >
                {paintMGCCodeDescInternal(
                  segment,
                  precedingCode,
                  precedingName,
                  showAsInactive,
                  3
                )}
              </li>
            )
        )}
      </ul>
    );
  }
  function paintMGCCodeDescInternal(
    metaObj,
    precedingCode,
    precedingName,
    showAsInactive,
    paddingLeft
  ) {
    let currentObjCode = precedingCode + ' ' + metaObj.code;
    let currentObjName = precedingName + ' ' + metaObj.name;

    return (
      <Box sx={{ paddingLeft: paddingLeft }}>
        {paintMGCCodeDesc(currentObjCode, currentObjName, showAsInactive)}
      </Box>
    );
  }

  function paintMinor(
    idPropName,
    metaObj,
    childrenPropName,
    precedingCode,
    precedingName,
    paddingLeft,
    defaultExpanded,
    selectedChildrenMap
  ) {
    let children = metaObj[childrenPropName];
    let hasChildren = children && children.length > 0;
    let currentObjCode = precedingCode + ' ' + metaObj.code;
    let currentObjName = precedingName + ' ' + metaObj.name;
    let canPaintBadge =
      selectedChildrenMap && children
        ? selectedChildrenMap.size < children.length
        : false;

    return hasChildren ? (
      <Accordion
        id={idPropName + metaObj.code}
        defaultExpanded={defaultExpanded}
        sx={{
          border: 'none'
        }}
      >
        <AccordionSummary>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {paintMGCCodeDesc(currentObjCode, currentObjName, false)}
            {canPaintBadge && (
              <Typography
                variant="labelSmall"
                sx={{
                  color: 'success.main',
                  fontWeight: theme.fuel6Tokens.JddcTypographyFontWeightBold,
                  pl: 3
                }}
              >
                {`${selectedChildrenMap.size}/${children.length} Included`}
              </Typography>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {selectedChildrenMap ? (
            <>
              <Typography fontWeight="bold">Included in Strategy</Typography>

              {paintSelectedAndNoTSelectedSegments(
                children,
                selectedChildrenMap,
                currentObjCode,
                currentObjName,
                false,
                true
              )}
              <hr />
              <Typography fontWeight="bold">
                Not Included in Strategy
              </Typography>
              {paintSelectedAndNoTSelectedSegments(
                children,
                selectedChildrenMap,
                currentObjCode,
                currentObjName,
                true,
                false
              )}
              <hr />
            </>
          ) : (
            children.map((segment) =>
              // Paint all Segments
              paintMinor(
                metaObj.code + '_segment',
                segment,
                null,
                currentObjCode,
                currentObjName,
                3
              )
            )
          )}
        </AccordionDetails>
      </Accordion>
    ) : (
      paintMGCCodeDescInternal(
        metaObj,
        precedingCode,
        precedingName,
        false,
        paddingLeft
      )
    );
  }

  function prepareSegmentsForPainting(editedMgcs) {
    let segments = [];

    editedMgcs &&
      editedMgcs.sort().forEach((mgc) => {
        if (mgc && mgc.segmentName) {
          let commodityCode = mgc.commodity;
          let majorCode = mgc.major;
          let minorCode = mgc.minor;
          let code =
            commodityCode +
            ' ' +
            majorCode +
            ' ' +
            minorCode +
            ' ' +
            mgc.segment;
          let desc =
            mgc.commodityDesc +
            ' ' +
            mgc.majorDesc +
            ' ' +
            mgc.minorName.substring(
              (commodityCode + ' ' + majorCode + ' ' + minorCode).length
            ) +
            ' ' +
            mgc.segmentName.substring(code.length);
          segments.push(code + ' - ' + desc);
        }
      });
    return { decorated: { mgcs: segments } };
  }

  // Component Painting
  let mgcDefLevel = getMGCDefinitionLevelAndSelections(selectedData);
  let selectedMasterMGCInfo =
    !mgcDefLevel?.segmentLevel &&
    findMasterMGCInfoForSelection(masterData, selectedData);

  return mgcDefLevel?.segmentLevel ? (
    // Paint just Segments
    isMgcEdited || props.editStrategy ? (
      <Box container sx={{ marginLeft: 3 }}>
        {paintMGCs(
          isMgcEdited ? prepareSegmentsForPainting(mgcs) : strategyInformation
        )}
      </Box>
    ) : (
      paintMGCs(strategyInformation)
    )
  ) : mgcDefLevel?.majorLevel ? (
    <>
      <Box container sx={{ marginLeft: 3 }}>
        {
          // Paint Major Code Desc
          paintMGCCodeDesc(
            selectedMasterMGCInfo?.commMajorCode,
            selectedMasterMGCInfo?.commMajorDesc
          )
        }
      </Box>
      {isMgcEdited &&
        mgcDefLevel &&
        mgcDefLevel.minorMap &&
        mgcDefLevel.minorMap.size > 0 &&
        selectedMasterMGCInfo.major?.minors &&
        selectedMasterMGCInfo.major?.minors.map((minor) =>
          // Paint all Minors
          mgcDefLevel?.minorMap.has(minor.code)
            ? paintMinor(
                'minor',
                minor,
                'segments',
                selectedMasterMGCInfo?.commMajorCode,
                selectedMasterMGCInfo?.commMajorDesc,
                9,
                mgcDefLevel?.minorMap.size === 1,
                mgcDefLevel?.minorMap.get(minor.code)
              )
            : ''
        )}

      {!isMgcEdited &&
        selectedMasterMGCInfo.major?.minors &&
        selectedMasterMGCInfo.major?.minors.map((minor) =>
          // Paint all Minors
          paintMinor(
            'minor',
            minor,
            'segments',
            selectedMasterMGCInfo?.commMajorCode,
            selectedMasterMGCInfo?.commMajorDesc,
            9
          )
        )}
    </>
  ) : (
    <>
      {selectedMasterMGCInfo &&
        selectedMasterMGCInfo.major?.minors &&
        selectedMasterMGCInfo.major?.minors.map((minor) => {
          // Paint only selected Minors
          return (
            mgcDefLevel?.minorMap.has(minor.code) &&
            paintMinor(
              'minor',
              minor,
              'segments',
              selectedMasterMGCInfo?.commMajorCode,
              selectedMasterMGCInfo?.commMajorDesc,
              9,
              mgcDefLevel?.minorMap.size === 1,
              mgcDefLevel?.minorMap.get(minor.code)
            )
          );
        })}
    </>
  );
}
